import React, { useEffect, useState } from 'react';
import { X, Download, BellRing, Smartphone } from 'lucide-react';
import toast from 'react-hot-toast';

const DIAS = 7;
const guardado = (k) => { try { const t = parseInt(localStorage.getItem(k), 10); return t && Date.now() - t < DIAS * 86400000; } catch { return false; } };
const guardar = (k) => { try { localStorage.setItem(k, String(Date.now())); } catch { /* modo privado */ } };

export const esMovil = () => /iphone|ipad|ipod|android/i.test(navigator.userAgent);
export const instalada = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
const esIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const soportaPush = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

let eventoInstalar = null;
window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); eventoInstalar = e; });

const aBytes = (base64) => {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
};

// Registra este dispositivo para recibir los avisos al instante
export async function activarAvisos(api) {
  if (!soportaPush()) return 'no_soportado';
  if (esIOS() && !instalada()) return 'instalar_primero';
  const permiso = await Notification.requestPermission();
  if (permiso !== 'granted') return permiso;
  const { data } = await api.get('/push/clave');
  if (!data.clave) return 'no_soportado';
  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: aBytes(data.clave) });
  await api.post('/push/suscribir', { subscription: sub.toJSON() });
  return 'granted';
}

export async function avisosActivos() {
  if (!soportaPush() || Notification.permission !== 'granted') return false;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg && await reg.pushManager.getSubscription();
  return !!sub;
}

/* En el celular propone instalar la app y activar avisos; en la compu, solo lo menciona */
export default function AppMovil({ api }) {
  const [modo, setModo] = useState(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (esMovil()) {
        if (!instalada()) { if (!guardado('portal_instalar')) setModo('instalar'); return; }
        if (soportaPush() && Notification.permission === 'default' && !guardado('portal_avisos') && !(await avisosActivos().catch(() => false))) setModo('avisos');
        return;
      }
      if (!guardado('portal_movil')) setModo('escritorio');
    }, 2500);
    return () => clearTimeout(t);
  }, []);

  if (!modo) return null;
  const cerrar = () => { guardar(modo === 'instalar' ? 'portal_instalar' : modo === 'avisos' ? 'portal_avisos' : 'portal_movil'); setModo(null); };

  const instalar = async () => {
    if (eventoInstalar) {
      eventoInstalar.prompt();
      const r = await eventoInstalar.userChoice.catch(() => null);
      eventoInstalar = null;
      if (r && r.outcome === 'accepted') { toast.success('¡Listo! Abrila desde el ícono de tu inicio'); return cerrar(); }
    }
    toast('En el menú del navegador elegí "Agregar a la pantalla de inicio"', { duration: 7000, icon: '📲' });
    cerrar();
  };

  const activar = async () => {
    const r = await activarAvisos(api).catch(() => 'error');
    if (r === 'granted') toast.success('Listo, te avisamos al instante');
    else if (r === 'denied') toast.error('Quedaron bloqueadas. Se habilitan desde los ajustes del celular.', { duration: 7000 });
    else if (r === 'instalar_primero') toast('Primero agregá la app a tu inicio y después activalas', { duration: 6000 });
    cerrar();
  };

  // En escritorio es solo una línea discreta abajo
  if (modo === 'escritorio') {
    return (
      <div style={{ position: 'fixed', left: 16, bottom: 16, zIndex: 1400, maxWidth: 330, display: 'flex', gap: 10, alignItems: 'center',
        background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '10px 12px', boxShadow: 'var(--shadow)' }}>
        <Smartphone size={17} style={{ color: 'var(--accent)', flexShrink: 0 }} />
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          Abrí este portal desde el celular y agregalo a tu inicio para seguir todo al instante.
        </div>
        <button onClick={cerrar} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 2 }}><X size={15} /></button>
      </div>
    );
  }

  const esInstalar = modo === 'instalar';
  return (
    <div style={{ position: 'fixed', left: 12, right: 12, bottom: 12, zIndex: 1700, display: 'flex', gap: 12, alignItems: 'center',
      background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.3)' }}>
      <div style={{ width: 40, height: 40, borderRadius: 11, background: 'var(--accent-glow)', color: 'var(--accent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {esInstalar ? <Download size={20} /> : <BellRing size={20} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{esInstalar ? 'Tenelo a mano' : 'Enterate al instante'}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
          {esInstalar ? 'Agregá el portal a tu pantalla de inicio' : 'Avisos de pagos, avances y reportes'}
        </div>
      </div>
      <button className="btn btn-primary btn-sm" onClick={esInstalar ? instalar : activar}>{esInstalar ? 'Agregar' : 'Activar'}</button>
      <button onClick={cerrar} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}><X size={16} /></button>
    </div>
  );
}
